import { decryptText, encryptText } from "./OstreonEncryption";
import { decryptBase64file } from "./UploadFile";
import { personSharingFlags } from "./partnerPropose";
import { sendUIErrorToBackend } from "./sendErrorToBackend";
const aes_pattern = /^[0-9a-fA-F]{16}-[0-9a-fA-F]{24}-[0-9a-fA-F]+$/;
export const encryptFiles = async (private_key, assetFiles) => {
  const new_assetFiles = await Promise.all(
    assetFiles.map(async (e) => {
      var newFile = {
        id: e?.id,
        name: "",
        lastModified: "",
        size: "",
      };
      newFile.name = await encryptText(private_key, e.name);
      newFile.size = await encryptText(private_key, e.size);
      newFile.lastModified = await encryptText(private_key, e.lastModified);
      return newFile;
    })
  );
  return JSON.stringify(new_assetFiles);
};
export const decryptFiles = async (private_key, assetFiles) => {
  const new_assetFiles = await Promise.all(
    assetFiles.map(async (e) => {
      var newFile = {
        id: e?.id,
        name: "",
        lastModified: "",
        size: "",
      };
      newFile.name = await decryptText(private_key, e.name);
      newFile.size = await decryptText(private_key, e.size);
      newFile.lastModified = await decryptText(private_key, e.lastModified);
      return newFile;
    })
  );
  return new_assetFiles;
};
/*
  The files a customer shares with their partner.

  Their own codec rather than encryptFiles above, for two reasons: these
  carry a folder path and a media type that an asset's documents do not,
  and widening the asset codec to fit would have put two features in one
  function that is already rebuilt field by field.

  Everything describing the file is encrypted, the folder path included --
  "Divorce/2019" says as much as a filename does. Only the id stays
  readable, because the server needs it to answer "may this partner
  download X" and to know the file is not an orphan; it is 32 random
  characters and describes nothing.
*/
export const encryptPartnerFiles = async (private_key, files) => {
  const list = Array.isArray(files) ? files : [];
  const encrypted = await Promise.all(
    list.map(async (file) => ({
      id: file?.id,
      name: await encryptText(private_key, String(file?.name ?? "")),
      size: await encryptText(private_key, String(file?.size ?? "")),
      lastModified: await encryptText(
        private_key,
        String(file?.lastModified ?? "")
      ),
      // "" at the root; "Design" or "Design/2019" inside an uploaded folder.
      path: await encryptText(private_key, String(file?.path ?? "")),
      // The browser's own type, for the Type column and the row's icon.
      mime: await encryptText(private_key, String(file?.mime ?? "")),
    }))
  );
  return JSON.stringify(encrypted);
};

export const decryptPartnerFiles = async (private_key, raw) => {
  const list = safeParseArray(raw);
  const files = await Promise.all(
    list.map(async (file) => {
      try {
        return {
          id: file?.id,
          name: await decryptText(private_key, file?.name),
          size: await decryptText(private_key, file?.size),
          lastModified: await decryptText(private_key, file?.lastModified),
          path: await decryptText(private_key, file?.path),
          mime: await decryptText(private_key, file?.mime),
        };
      } catch (error) {
        /*
          One unreadable entry does not take the list with it. A file whose
          metadata will not open cannot be shown or downloaded usefully, so
          it is dropped rather than drawn as a row of ciphertext.
        */
        console.log(error);
        return null;
      }
    })
  );
  return files.filter(Boolean);
};

export const encryptIssues = (e, private_key) => {
  return Promise.all(
    e.map((issue_type) => {
      return Promise.all(
        issue_type.map(async (issue) => {
          var new_issue = {
            type: issue.type,
            id: issue?.id,
            text: "",
          };
          new_issue.text = await encryptText(private_key, issue.text);
          // console.log(new_issue);
          return new_issue;
        })
      );
    })
  );
};
export const decryptIssues = (e, private_key) => {
  return Promise.all(
    e.map((issue_type) => {
      return Promise.all(
        issue_type.map(async (issue) => {
          var new_issue = {
            type: issue.type,
            id: issue?.id,
            text: "",
          };
          new_issue.text =
            (await decryptText(private_key, issue.text)) || "Unknown";
          return new_issue;
        })
      );
    })
  );
};

/*
  ---------------------------------------------------------------------------
  Asset Evaluation helpers
  ---------------------------------------------------------------------------
  Everything below follows the encryption already used across this file: each
  value is AES-GCM encrypted in the browser under the user's ostreon_key via
  encryptText, and arrays are stored as a JSON string whose element ids stay in
  plaintext — the same shape encryptFiles/decryptFiles already produce for
  asset_files.

  These helpers must NEVER throw. decryptAsset is a single linear try block that
  resolves a half-built object on any error, so one bad parse on a new field
  would blank an asset's name, type and credentials. Assets created before Asset
  Evaluation existed have all of these fields undefined, which is exactly the
  case the guards cover.
*/

// Encrypt one scalar. Undefined/null become "" rather than the string "undefined".
const encScalar = async (private_key, value) => {
  if (value === undefined || value === null) return "";
  return encryptText(private_key, JSON.stringify(value));
};

// Decrypt one scalar written by encScalar. Falls back instead of throwing.
const decScalar = async (private_key, value, fallback = "") => {
  if (!value) return fallback;
  try {
    const plain = await decryptText(private_key, value);
    if (plain === undefined || plain === null || plain === "") return fallback;
    const parsed = JSON.parse(plain);
    return parsed === null || parsed === undefined ? fallback : parsed;
  } catch (error) {
    return fallback;
  }
};

/*
  Array-typed asset fields (beneficiaries, trustees, asset_files) must come back
  as arrays or every consumer that calls .map() on them crashes.

  The trap: decryptText returns the literal string `""` when decryption fails,
  and JSON.parse('""') yields an empty STRING, not an array. Nothing throws, so
  decryptAsset's catch never fires — the asset looks intact apart from one field
  silently being the wrong type, and the Edit Asset modal dies rendering it.
  Optional chaining does not help, because "" is not null.

  Also accepts a plain (unencrypted) JSON array, which is how `trustees` is
  stored on assets saved before that field was encrypted.
*/
const decList = async (private_key, value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const plain = aes_pattern.test(value)
      ? await decryptText(private_key, value)
      : value;
    const parsed = JSON.parse(plain);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

/*
  A field that holds a JSON string (credentials). Returns the fallback when it
  cannot be decrypted, so callers that JSON.parse it again do not blow up on
  `JSON.parse("")` -> "Unexpected end of JSON input".
*/
const NO_CREDENTIALS = '{"record_name":"This assets has no credentials"}';

const decJsonString = async (private_key, value, fallback) => {
  if (!value) return fallback;
  try {
    const plain = await decryptText(private_key, value);
    const parsed = JSON.parse(plain);
    if (typeof parsed !== "string" || parsed === "") return fallback;
    JSON.parse(parsed); // must itself be parseable — that is what callers do
    return parsed;
  } catch (error) {
    return fallback;
  }
};

/*
  True when a stored value looks encrypted but did not decrypt.

  decryptText returns the literal `""` on failure, so a field can silently come
  back empty while the ciphertext behind it is perfectly good — it was simply
  written under a different key (an interrupted password change leaves assets
  like this). Re-encrypting that empty value on the next save would overwrite
  the original ciphertext and destroy data the user never got to see, so the
  fields that fail are recorded and their ciphertext is passed through untouched
  by encryptAsset.
*/
const decryptFailed = async (private_key, value) => {
  if (!value || typeof value !== "string" || !aes_pattern.test(value)) return false;
  const plain = await decryptText(private_key, value);
  return plain === '""';
};

// Tolerates undefined, a JSON string, or an already-parsed array.
const safeParseArray = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

/*
  Evaluation and debt record series. Same shape for both — the only difference
  is that debt values are always negative. `id` and `created_at` are assigned by
  the frontend, since the server never sees plaintext and cannot assign either.
*/
export const encryptRecords = async (private_key, records) => {
  const list = Array.isArray(records) ? records : [];
  const encrypted = await Promise.all(
    list.map(async (record) => ({
      id: record?.id,
      date: await encScalar(private_key, record?.date),
      value: await encScalar(private_key, record?.value),
      currency: await encScalar(private_key, record?.currency),
      created_at: await encScalar(private_key, record?.created_at),
    }))
  );
  return JSON.stringify(encrypted);
};

export const decryptRecords = async (private_key, raw) => {
  const list = safeParseArray(raw);
  return Promise.all(
    list.map(async (record) => ({
      id: record?.id,
      date: await decScalar(private_key, record?.date, ""),
      value: await decScalar(private_key, record?.value, 0),
      currency: await decScalar(private_key, record?.currency, ""),
      created_at: await decScalar(private_key, record?.created_at, null),
    }))
  );
};

/*
  "New Field" blocks. A block carries only the field set matching its `type`;
  the other is dropped, so switching type in the UI is non-destructive locally
  but never persists both. `id` and `type` stay plaintext so the backend can do
  its structural check without a key.
*/
export const encryptDataEntries = async (private_key, entries) => {
  const list = Array.isArray(entries) ? entries : [];
  const encrypted = await Promise.all(
    list.map(async (entry) => {
      if (entry?.type === "other_owners") {
        return {
          id: entry?.id,
          type: "other_owners",
          my_ownership: await encScalar(private_key, entry?.my_ownership),
          owners: await Promise.all(
            (Array.isArray(entry?.owners) ? entry.owners : []).map(async (owner) => ({
              id: owner?.id,
              owner: await encScalar(private_key, owner?.owner),
              ownership: await encScalar(private_key, owner?.ownership),
            }))
          ),
        };
      }
      /*
        A location is a country, an income is an amount and its currency. Both
        are the user's own facts about the asset, so both are encrypted like
        everything else — only the id and the type stay readable, because the
        server sorts and validates on them.
      */
      if (entry?.type === "location") {
        return {
          id: entry?.id,
          type: "location",
          country: await encScalar(private_key, entry?.country),
        };
      }
      if (entry?.type === "annual_income" || entry?.type === "monthly_income") {
        return {
          id: entry?.id,
          type: entry.type,
          amount: await encScalar(private_key, entry?.amount),
          currency: await encScalar(private_key, entry?.currency),
        };
      }
      return {
        id: entry?.id,
        type: "custom",
        custom_fields: await Promise.all(
          (Array.isArray(entry?.custom_fields) ? entry.custom_fields : []).map(
            async (field) => ({
              id: field?.id,
              title: await encScalar(private_key, field?.title),
              value: await encScalar(private_key, field?.value),
            })
          )
        ),
      };
    })
  );
  return JSON.stringify(encrypted);
};

export const decryptDataEntries = async (private_key, raw) => {
  const list = safeParseArray(raw);
  return Promise.all(
    list.map(async (entry) => {
      if (entry?.type === "other_owners") {
        return {
          id: entry?.id,
          type: "other_owners",
          my_ownership: await decScalar(private_key, entry?.my_ownership, ""),
          owners: await Promise.all(
            (Array.isArray(entry?.owners) ? entry.owners : []).map(async (owner) => ({
              id: owner?.id,
              owner: await decScalar(private_key, owner?.owner, ""),
              ownership: await decScalar(private_key, owner?.ownership, ""),
            }))
          ),
          custom_fields: [],
        };
      }
      if (entry?.type === "location") {
        return {
          id: entry?.id,
          type: "location",
          my_ownership: "",
          owners: [],
          custom_fields: [],
          country: await decScalar(private_key, entry?.country, ""),
          amount: "",
          currency: "",
        };
      }
      if (entry?.type === "annual_income" || entry?.type === "monthly_income") {
        return {
          id: entry?.id,
          type: entry.type,
          my_ownership: "",
          owners: [],
          custom_fields: [],
          country: "",
          amount: await decScalar(private_key, entry?.amount, ""),
          currency: await decScalar(private_key, entry?.currency, ""),
        };
      }
      return {
        id: entry?.id,
        type: "custom",
        my_ownership: "",
        owners: [],
        custom_fields: await Promise.all(
          (Array.isArray(entry?.custom_fields) ? entry.custom_fields : []).map(
            async (field) => ({
              id: field?.id,
              title: await decScalar(private_key, field?.title, ""),
              value: await decScalar(private_key, field?.value, ""),
            })
          )
        ),
      };
    })
  );
};

/*
  Per-user custom financial-exposure types. `value` is a stable plaintext id
  that an asset's financial_exposure points at; only the user-typed label is
  encrypted. Lives on the user document, never on an asset, which is what keeps
  one user's list out of everyone else's dropdown.
*/
export const encryptExposureOptions = async (private_key, options) => {
  const list = Array.isArray(options) ? options : [];
  const encrypted = await Promise.all(
    list.map(async (option) => ({
      value: option?.value,
      label: await encScalar(private_key, option?.label),
    }))
  );
  return JSON.stringify(encrypted);
};

export const decryptExposureOptions = async (private_key, raw) => {
  const list = safeParseArray(raw);
  const decrypted = await Promise.all(
    list.map(async (option) => ({
      value: option?.value,
      label: await decScalar(private_key, option?.label, ""),
    }))
  );
  return decrypted.filter((option) => option.value && option.label);
};

export const encryptAsset = async (e, privateKEY) => {
  return new Promise(async (resolve, reject) => {
    try {
      var new_asset = {
        asset_id: e.asset_id,
        asset_files: "",
        asset_name: "",
        asset_type: "",
        credentials: "",
        description: "",
        beneficiaries: "",
        trustees: "",
        access: e.access,
        /*
          Partner sharing flags travel in the clear, like asset_id and access.
          That is the point of them: the server has to be able to filter on them
          to decide what a partner may be sent, and it can never read anything
          that is encrypted here.
        */
        share_with_partner: Boolean(e.share_with_partner),
        share_description: e.share_description !== false,
        share_files: e.share_files !== false,
        share_evaluations: e.share_evaluations !== false,
        share_beneficiaries: e.share_beneficiaries !== false,
        share_trusted_advisor: e.share_trusted_advisor !== false,
        /*
          What the partner may CHANGE, and what they have asked to change.

          In the clear for the same reason as the flags above: the edit
          endpoint has to check a proposal against them without being able to
          read anything. They default to FALSE here -- an older asset that
          predates them has granted nothing, and `!== false` would read a
          missing flag as permission.

          `partner_changes` passes through untouched. Each value in it was
          already encrypted under this key by whoever proposed it; encrypting
          it again here would leave the customer unable to read their own
          pending changes.

          Named explicitly because this function rebuilds the asset field by
          field: anything not named is dropped on the next save.
        */
        edit_description: e.edit_description === true,
        edit_files: e.edit_files === true,
        edit_evaluations: e.edit_evaluations === true,
        edit_beneficiaries: e.edit_beneficiaries === true,
        edit_trusted_advisor: e.edit_trusted_advisor === true,
        partner_changes: Array.isArray(e.partner_changes) ? e.partner_changes : [],
        /*
          Partner-created and awaiting the customer's approval, in the clear for
          the same reason as the flags above -- the server has to exclude these
          without reading them.

          Named here because this function rebuilds the asset field by field:
          left off, an asset the partner created would lose its pending mark the
          first time the customer's browser loaded and saved, and go live
          without anyone approving it.
        */
        pending_approval: e.pending_approval === true,
        created_by_partner: e.created_by_partner || "",
        // Asset Evaluation — see the helpers at the top of this file.
        financial_exposure: "",
        evaluation_date: "",
        asset_value: "",
        currency: "",
        is_liquid: "",
        has_associated_debt: "",
        debt_type: "",
        debt_amount: "",
        debt_currency: "",
        debt_annual_interest: "",
        debt_payoff_date: "",
        last_updated: "",
        asset_evaluation_records: "",
        asset_debt_records: "",
        data_entries: "",
        assets_order: "",
        asset_management_order: "",
      };
      // const encrypted_asset_files = await encryptAssetFiles(privateKEY, e.asset_files)
      /*
        Fields whose ciphertext could not be decrypted on the way in are written
        back byte-for-byte. Re-encrypting the empty placeholder the UI showed
        would destroy the original, and the user never saw what was in it.
      */
      const undecrypted = e.__undecrypted || {};
      const passthrough = (field) =>
        Object.prototype.hasOwnProperty.call(undecrypted, field);
      for (const field of Object.keys(undecrypted)) {
        new_asset[field] = undecrypted[field];
      }

      if (!passthrough("asset_files"))
        new_asset.asset_files = await encryptFiles(privateKEY, e.asset_files);
      if (!passthrough("asset_name"))
      new_asset.asset_name = await encryptText(
        privateKEY,
        JSON.stringify(e.asset_name)
      );
      if (!passthrough("asset_type"))
      new_asset.asset_type = await encryptText(
        privateKEY,
        JSON.stringify(e.asset_type)
      );
      if (!passthrough("credentials"))
      new_asset.credentials = await encryptText(
        privateKEY,
        JSON.stringify(e.credentials)
      );
      if (!passthrough("description"))
      new_asset.description = await encryptText(
        privateKEY,
        JSON.stringify(e.description)
      );
      if (!passthrough("beneficiaries"))
      new_asset.beneficiaries = await encryptText(
        privateKEY,
        JSON.stringify(e.beneficiaries)
      );
      // new_asset.trustees = await encryptText(privateKEY, JSON.stringify(e.trustees));
      if (!passthrough("trustees")) new_asset.trustees = JSON.stringify(e.trustees);

      /*
        Asset Evaluation. has_associated_debt is the source of truth for the
        debt fields: when it is false the debt values are cleared rather than
        persisted, so toggling it off actually removes the debt instead of
        leaving stale numbers behind for the next read to resurrect.
      */
      const debtOn = e.has_associated_debt === true || e.has_associated_debt === "true";
      new_asset.financial_exposure = await encScalar(privateKEY, e.financial_exposure);
      new_asset.evaluation_date = await encScalar(privateKEY, e.evaluation_date);
      new_asset.asset_value = await encScalar(privateKEY, e.asset_value);
      new_asset.currency = await encScalar(privateKEY, e.currency);
      new_asset.is_liquid = await encScalar(privateKEY, e.is_liquid === true || e.is_liquid === "true");
      new_asset.has_associated_debt = await encScalar(privateKEY, debtOn);
      new_asset.debt_type = await encScalar(privateKEY, debtOn ? e.debt_type : "");
      new_asset.debt_amount = await encScalar(privateKEY, debtOn ? e.debt_amount : "");
      new_asset.debt_currency = await encScalar(privateKEY, debtOn ? e.debt_currency : "");
      new_asset.debt_annual_interest = await encScalar(privateKEY, debtOn ? e.debt_annual_interest : "");
      new_asset.debt_payoff_date = await encScalar(privateKEY, debtOn ? e.debt_payoff_date : "");
      // Set by the save effect in DataContext, not by any editor.
      new_asset.last_updated = await encScalar(privateKEY, e.last_updated);
      new_asset.asset_evaluation_records = await encryptRecords(privateKEY, e.asset_evaluation_records);
      new_asset.asset_debt_records = await encryptRecords(privateKEY, e.asset_debt_records);
      new_asset.data_entries = await encryptDataEntries(privateKEY, e.data_entries);
      // Manual row order, one per table. Encrypted like everything else.
      new_asset.assets_order = await encScalar(privateKEY, e.assets_order);
      new_asset.asset_management_order = await encScalar(
        privateKEY,
        e.asset_management_order
      );

      resolve(new_asset);
    } catch (error) {
      console.log(error);
      reject(error);
    }
  });
};
export const decryptAsset = async (e, private_key) => {
  return new Promise(async (resolve, reject) => {
    try {
      var new_asset = {
        asset_id: e.asset_id,
        asset_files: "",
        asset_name: "",
        asset_type: "",
        credentials: "",
        description: "",
        // Arrays, so a failure anywhere above still yields a renderable asset.
        beneficiaries: [],
        trustees: [],
        access: e.access,
        /*
          Partner sharing flags travel in the clear, like asset_id and access.
          That is the point of them: the server has to be able to filter on them
          to decide what a partner may be sent, and it can never read anything
          that is encrypted here.
        */
        share_with_partner: Boolean(e.share_with_partner),
        share_description: e.share_description !== false,
        share_files: e.share_files !== false,
        share_evaluations: e.share_evaluations !== false,
        share_beneficiaries: e.share_beneficiaries !== false,
        share_trusted_advisor: e.share_trusted_advisor !== false,
        /*
          What the partner may CHANGE, and what they have asked to change.

          In the clear for the same reason as the flags above: the edit
          endpoint has to check a proposal against them without being able to
          read anything. They default to FALSE here -- an older asset that
          predates them has granted nothing, and `!== false` would read a
          missing flag as permission.

          `partner_changes` passes through untouched. Each value in it was
          already encrypted under this key by whoever proposed it; encrypting
          it again here would leave the customer unable to read their own
          pending changes.

          Named explicitly because this function rebuilds the asset field by
          field: anything not named is dropped on the next save.
        */
        edit_description: e.edit_description === true,
        edit_files: e.edit_files === true,
        edit_evaluations: e.edit_evaluations === true,
        edit_beneficiaries: e.edit_beneficiaries === true,
        edit_trusted_advisor: e.edit_trusted_advisor === true,
        partner_changes: Array.isArray(e.partner_changes) ? e.partner_changes : [],
        /*
          Partner-created and awaiting the customer's approval, in the clear for
          the same reason as the flags above -- the server has to exclude these
          without reading them.

          Named here because this function rebuilds the asset field by field:
          left off, an asset the partner created would lose its pending mark the
          first time the customer's browser loaded and saved, and go live
          without anyone approving it.
        */
        pending_approval: e.pending_approval === true,
        created_by_partner: e.created_by_partner || "",
        // Asset Evaluation defaults. An asset saved before this feature existed
        // has every one of these undefined, and must still open normally.
        financial_exposure: "",
        evaluation_date: "",
        asset_value: "",
        currency: "",
        is_liquid: false,
        has_associated_debt: false,
        debt_type: "",
        debt_amount: "",
        debt_currency: "",
        debt_annual_interest: "",
        debt_payoff_date: "",
        last_updated: "",
        asset_evaluation_records: [],
        asset_debt_records: [],
        data_entries: [],
        // Absent on assets that predate the field; the tables fall back to the
        // asset's position, so nothing moves until something is dragged.
        assets_order: "",
        asset_management_order: "",
      };
      // safeParseArray rather than JSON.parse: a malformed value here would
      // throw and drop the entire asset into the catch below.
      new_asset.asset_files = await decryptFiles(
        private_key,
        safeParseArray(e.asset_files)
      );
      new_asset.asset_name = JSON.parse(
        await decryptText(private_key, e.asset_name, "asset_name")
      );
      new_asset.asset_type = JSON.parse(
        await decryptText(private_key, e.asset_type, "asset_type")
      );
      new_asset.credentials = await decJsonString(
        private_key,
        e.credentials,
        NO_CREDENTIALS
      );
      new_asset.description = JSON.parse(
        await decryptText(private_key, e.description, "description")
      );
      new_asset.beneficiaries = await decList(private_key, e.beneficiaries);
      // decList handles both the encrypted form and the older plain-JSON one.
      new_asset.trustees = await decList(private_key, e.trustees);

      /*
        Asset Evaluation. Every helper below is guaranteed not to throw, which
        matters because the catch on this try resolves a half-built asset — a
        single bad parse here would blank the name, type and credentials of
        every asset created before this feature shipped.
      */
      new_asset.financial_exposure = await decScalar(private_key, e.financial_exposure, "");
      new_asset.evaluation_date = await decScalar(private_key, e.evaluation_date, "");
      new_asset.asset_value = await decScalar(private_key, e.asset_value, "");
      new_asset.currency = await decScalar(private_key, e.currency, "");
      new_asset.is_liquid = (await decScalar(private_key, e.is_liquid, false)) === true;
      new_asset.has_associated_debt = (await decScalar(private_key, e.has_associated_debt, false)) === true;
      new_asset.debt_type = await decScalar(private_key, e.debt_type, "");
      new_asset.debt_amount = await decScalar(private_key, e.debt_amount, "");
      new_asset.debt_currency = await decScalar(private_key, e.debt_currency, "");
      new_asset.debt_annual_interest = await decScalar(private_key, e.debt_annual_interest, "");
      new_asset.debt_payoff_date = await decScalar(private_key, e.debt_payoff_date, "");
      new_asset.last_updated = await decScalar(private_key, e.last_updated, "");
      new_asset.asset_evaluation_records = await decryptRecords(private_key, e.asset_evaluation_records);
      new_asset.asset_debt_records = await decryptRecords(private_key, e.asset_debt_records);
      new_asset.data_entries = await decryptDataEntries(private_key, e.data_entries);
      new_asset.assets_order = await decScalar(private_key, e.assets_order, "");
      new_asset.asset_management_order = await decScalar(
        private_key,
        e.asset_management_order,
        ""
      );

      /*
        Record any field whose ciphertext refused to decrypt and keep the
        original bytes, so encryptAsset can pass them through instead of
        overwriting them with the empty value shown on screen.
      */
      const preserved = {};
      for (const field of [
        "asset_name",
        "asset_type",
        "credentials",
        "description",
        "beneficiaries",
        "trustees",
        "asset_files",
      ]) {
        // eslint-disable-next-line no-await-in-loop
        if (await decryptFailed(private_key, e[field])) preserved[field] = e[field];
      }
      if (Object.keys(preserved).length) new_asset.__undecrypted = preserved;

      resolve(new_asset);
    } catch (error) {
      console.log(error);
      resolve(new_asset);
    }
  });
};
export const encryptBeneficiary = async (e, private_key) => {
  return new Promise(async (resolve, reject) => {
    try {
      var new_beneficiary = {
        ...e,
        priority: String(e.priority),
        name: null,
        personal_documents: null,
        birth_date: null,
        status: null,
        // profile_avatar: null,
      };
      delete new_beneficiary.profile_avatar;
      new_beneficiary.name = await encryptText(
        private_key,
        JSON.stringify(e.name)
      );
      new_beneficiary.personal_documents = await encryptFiles(
        private_key,
        e.personal_documents
      );
      new_beneficiary.birth_date = await encryptText(
        private_key,
        JSON.stringify(String(e.birth_date))
      );
      new_beneficiary.message = await encryptText(
        private_key,
        JSON.stringify(String(e.message))
      );
      new_beneficiary.status = await encryptText(
        private_key,
        JSON.stringify(String(e.status))
      );
      /*
        Encrypted here beside `birth_date`, being the user's own words about a
        person.

        `?? ""` rather than String(): a beneficiary saved before this field
        existed does not have it, and String(undefined) would write the four
        letters "undefined" into the record and read them back as a
        relationship.
      */
      new_beneficiary.relationship = await encryptText(
        private_key,
        JSON.stringify(e.relationship ?? "")
      );

      resolve(new_beneficiary);
    } catch (error) {
      console.log("Errorrrr");
      reject(error);
    }
  });
};
export const decryptBeneficiary = async (e, private_key) => {
  return new Promise(async (resolve, reject) => {
    try {
      var new_beneficiary = {
        ...e,
        name: null,
        personal_documents: null,
        birth_date: null,
        message: null,
        accessed: e.accessed,
        /*
          Every partner-sharing flag, from the canonical list.

          This rebuilds the beneficiary field by field, so anything not
          listed is dropped -- and these were listed by hand, which went
          wrong the moment the panel grew more rows: the new flags saved
          correctly and then vanished on the next page load, so the
          switches came back off with nothing to say why.

          Normalised to real booleans rather than left to a spread: a
          record written before a flag existed carries undefined, and the
          form's toggles need a false to render as off.
        */
        ...personSharingFlags(e),
        /*
          What the partner has asked to change. Passes through already
          encrypted -- see the asset skeleton above for why.
        */
        partner_changes: Array.isArray(e?.partner_changes) ? e.partner_changes : [],
      };

      new_beneficiary.name = JSON.parse(await decryptText(private_key, e.name));
      new_beneficiary.personal_documents = await decryptFiles(
        private_key,
        JSON.parse(e.personal_documents)
      );
      new_beneficiary.birth_date = JSON.parse(
        await decryptText(private_key, e.birth_date)
      );
      new_beneficiary.message = JSON.parse(
        await decryptText(private_key, e.message)
      );
      if (e?.profile_avatar) {
        const avatar_extension = e?.profile_avatar[0];
        if (avatar_extension == "/") {
          new_beneficiary.profile_avatar = e?.profile_avatar;
        } else if (avatar_extension == "i") {
          try {
            new_beneficiary.profile_avatar = await decryptBase64file(
              private_key,
              e?.profile_avatar
            );
          } catch (error) {
            new_beneficiary.profile_avatar = e?.profile_avatar;
          }
        } else {
          new_beneficiary.profile_avatar = await decryptBase64file(
            private_key,
            e?.profile_avatar
          );
        }
      }
      if (e.status)
        new_beneficiary.status = JSON.parse(
          await decryptText(private_key, e.status)
        );
      /*
        Read back only where it exists. Every beneficiary stored before this
        field did has none, and handing `undefined` to decryptText throws —
        which would reject this promise and lose the whole beneficiary, not just
        the one missing field.
      */
      new_beneficiary.relationship = e.relationship
        ? JSON.parse(await decryptText(private_key, e.relationship))
        : "";
      resolve(new_beneficiary);
    } catch (error) {
      console.log(error);
      console.log(new_beneficiary);
      reject(error);
    }
  });
};
export const encryptTrustee = (e, private_key) => {
  return new Promise(async (resolve, reject) => {
    try {
      let new_trustee = {
        ...e,
        user_id: e.user_id,
        name: "",
        email: "",
        phone_number: "",
        awareness_status: "",
        additional_information: "",
        profile_avatar: null,
      };
      new_trustee.name = await encryptText(private_key, JSON.stringify(e.name));
      new_trustee.email = await encryptText(
        private_key,
        JSON.stringify(e.email)
      );
      new_trustee.additional_information = await encryptText(
        private_key,
        JSON.stringify(e.additional_information)
      );
      new_trustee.phone_number = await encryptText(
        private_key,
        JSON.stringify(e.phone_number)
      );
      new_trustee.awareness_status = await encryptText(
        private_key,
        JSON.stringify(e.awareness_status)
      );
      //   e.profile_avatar &&
      //     (new_trustee.profile_avatar = await encryptText(
      //       private_key,
      //       JSON.stringify(e.profile_avatar)
      //     ));
      resolve(new_trustee);
    } catch (error) {
      reject(error);
    }
  });
};
export const decryptTrustee = (e, private_key) => {
  return new Promise(async (resolve, reject) => {
    try {
      let new_trustee = {
        user_id: e.user_id,
        name: "",
        email: "",
        phone_number: "",
        awareness_status: "",
        additional_information: "",
        profile_avatar: null,
        editable: e?.editable,
        /*
          Every partner-sharing flag, from the canonical list -- see the
          beneficiary above. This rebuilds the trustee field by field, and
          naming them here by hand is exactly what dropped the advisor's
          message and relation rows on every page load.
        */
        ...personSharingFlags(e),
        // What the partner has asked to change, already encrypted.
        partner_changes: Array.isArray(e?.partner_changes) ? e.partner_changes : [],
        // Partner-created and not yet approved; see Utils/partnerApproval.js.
        pending_approval: e?.pending_approval === true,
        created_by_partner: e?.created_by_partner || "",
      };
      new_trustee.name = JSON.parse(await decryptText(private_key, e.name));
      new_trustee.email = JSON.parse(await decryptText(private_key, e.email));
      new_trustee.additional_information = JSON.parse(
        await decryptText(private_key, e.additional_information)
      );
      new_trustee.phone_number = JSON.parse(
        await decryptText(private_key, e.phone_number)
      );
      try {
        new_trustee.awareness_status = JSON.parse(
          await decryptText(private_key, e.awareness_status)
        );
        if (new_trustee.awareness_status !== ("true" || "false"))
          throw new Error();
      } catch (error) {
        new_trustee.awareness_status = "false";
      }
      if (e?.profile_avatar) {
        const avatar_extension = e?.profile_avatar[0];
        if (avatar_extension == "/") {
          new_trustee.profile_avatar = e?.profile_avatar;
        } else if (avatar_extension == "i") {
          try {
            new_trustee.profile_avatar = await decryptBase64file(
              private_key,
              e?.profile_avatar
            );
          } catch (error) {
            new_trustee.profile_avatar = e?.profile_avatar;
          }
        } else {
          new_trustee.profile_avatar = await decryptBase64file(
            private_key,
            e?.profile_avatar
          );
        }
      }
      // new_trustee.profile_avatar = e.profile_avatar;
      //   e.profile_avatar &&
      //     (new_trustee.profile_avatar = JSON.parse(
      //       await decryptText(private_key, e.profile_avatar)
      //     ));
      resolve(new_trustee);
    } catch (error) {
      reject(error);
    }
  });
};
export const encryptLegal = async (legal, private_key) => {
  return new Promise(async (resolve, reject) => {
    try {
      var new_legal = {
        user_id: legal.user_id,
        name: legal.name,
        email: legal.email,
        phone_number: "",
        address: "",
        additional_information: "",
        notify: "",
        type: legal.type,
        website_url: "",
        associated_trustee:legal?.associated_trustee
      };
      new_legal.phone_number = await encryptText(
        private_key,
        legal.phone_number
      );
      new_legal.address = await encryptText(private_key, legal.address);
      new_legal.additional_information = await encryptText(
        private_key,
        legal.additional_information
      );
      new_legal.notify = await encryptText(private_key, legal.notify);
      if (legal.website_url)
        new_legal.website_url = await encryptText(
          private_key,
          legal.website_url
        );
      // new_legal.type= await encryptText(private_key, legal.type)

      resolve(new_legal);
    } catch (error) {
      reject(error);
    }
  });
};
export const decryptLegal = async (legal, private_key) => {
  return new Promise(async (resolve, reject) => {
    try {
      var new_legal = {
        user_id: legal.user_id,
        name: legal.name,
        email: legal.email,
        phone_number: "",
        address: "",
        additional_information: "",
        notify: "",
        type: legal.type,
        profile_avatar: null,
        website_url: legal?.website_url,
        associated_trustee:legal?.associated_trustee
      };
      new_legal.phone_number = await decryptText(
        private_key,
        legal.phone_number
      );
      new_legal.address = await decryptText(private_key, legal.address);
      new_legal.additional_information = await decryptText(
        private_key,
        legal.additional_information
      );
      new_legal.notify = await decryptText(private_key, legal.notify);
      new_legal.type = aes_pattern.test(legal.type)
        ? await decryptText(private_key, legal.type)
        : legal.type;
      new_legal.website_url = aes_pattern.test(legal.website_url)
        ? await decryptText(private_key, legal.website_url)
        : legal.website_url;
      if (legal?.profile_avatar) {
        // const avatar_extension = legal?.profile_avatar[0]
        try {
          new_legal.profile_avatar = await decryptBase64file(
            private_key,
            legal?.profile_avatar
          );
        } catch (error) {
          new_legal.profile_avatar = null;
        }
      }
      resolve(new_legal);
    } catch (error) {
      reject(error);
    }
  });
};
export const encryptData = async (UserData, privateKEY) => {
  return new Promise(async (resolve, reject) => {
    var new_data = {
      user_id: UserData?.user_id,
      customer_name: UserData?.customer_name,
      customer_last_name: UserData?.customer_last_name,
      plan: UserData?.plan,
      keep_alive_timeout: UserData?.keep_alive_timeout,
      last_login: UserData?.last_login,
      assets: "",
      beneficiaries: "",
      inheritance_mechanism_status: UserData?.inheritance_mechanism_status,
      issues: UserData?.issues,
      trustees: "",
      inheritance_parameters: UserData?.inheritance_parameters,
      legal: "",
      customer_phone_number: UserData.customer_phone_number,
      // Per-user custom financial-exposure types (Asset Evaluation).
      financial_exposure_custom_options: "",
      // Per-user Asset Management column choice.
      asset_management_presented_fields: "",
      // Per-user custom asset types.
      asset_type_custom_options: "",
      // Per-user owner names typed into an asset's ownership block.
      asset_owner_custom_options: "",
    };
    try {
      const new_assets = await Promise.all(
        UserData?.assets?.map((asset) => {
          return encryptAsset(asset, privateKEY);
        })
      );
      const new_beneficiaries = await Promise.all(
        UserData?.beneficiaries?.map((beneficiary) => {
          return encryptBeneficiary(beneficiary, privateKEY);
        })
      );
      const new_trustees = await Promise.all(
        UserData?.trustees?.map((trustee) => {
          return encryptTrustee(trustee, privateKEY);
        })
      );
      const new_issues = await encryptIssues(UserData.issues, privateKEY);
      const new_legal = await Promise.all(
        UserData?.legal?.map((e) => {
          return encryptLegal(e, privateKEY);
        })
      );
      // const customer_name = await encryptText(privateKEY, UserData.customer_name)
      // const customer_last_name = await encryptText(privateKEY, UserData.customer_last_name)
      // new_data.customer_name=customer_name
      // new_data.customer_last_name=customer_last_name
      new_data.assets = new_assets;
      new_data.beneficiaries = new_beneficiaries;
      new_data.trustees = new_trustees;
      new_data.issues = new_issues;
      new_data.legal = new_legal;
      new_data.financial_exposure_custom_options = await encryptExposureOptions(
        privateKEY,
        UserData?.financial_exposure_custom_options
      );
      /*
        null means "show everything" and must survive as null — encrypting an
        empty array would read back as "show nothing" and blank the table.
      */
      // Same {value,label} shape as the exposure list, so one helper serves both.
      new_data.asset_type_custom_options = await encryptExposureOptions(
        privateKEY,
        UserData?.asset_type_custom_options
      );
      /*
        Custom owners, same shape again. Only the label carries a name, and it
        is the encrypted half — the opaque `value` beside it is just the list's
        own key, so nothing here is readable without the user's private key.
      */
      new_data.asset_owner_custom_options = await encryptExposureOptions(
        privateKEY,
        UserData?.asset_owner_custom_options
      );
      new_data.asset_management_presented_fields = Array.isArray(
        UserData?.asset_management_presented_fields
      )
        ? await encScalar(privateKEY, UserData.asset_management_presented_fields)
        : "";
      /*
        The partner-shared file list is deliberately NOT sent here.

        This payload is the whole document as one screen sees it, and
        every screen that saves sends its own. A snapshot taken before an
        upload finished would carry an empty list and erase a file already
        on disk -- which is what it did. It has its own endpoint instead,
        saveSharedFiles in Utils/partnerFiles, and the server strips these
        two fields from this route so a stale copy cannot reach them.
      */
      resolve(new_data);
    } catch (e) {
      console.log(e);
      reject(e);
    }
  });
};
export const decryptData = async (encryptedata, private_key) => {
  return new Promise(async (resolve, reject) => {
    const new_data = {
      ...encryptedata, // create a new object with the same properties as encryptedata
      assets: [],
      beneficiaries: [],
      trustees: [],
    };
    try {
      const new_assets = await Promise.all(
        encryptedata.assets.map((asset) => {
          return decryptAsset(asset, private_key);
        })
      );
      const new_beneficiaries = await Promise.all(
        encryptedata.beneficiaries.map((beneficiary) => {
          return decryptBeneficiary(beneficiary, private_key);
        })
      );
      const new_trustees = await Promise.all(
        encryptedata.trustees.map((trustee) => {
          return decryptTrustee(trustee, private_key);
        })
      );
      const new_legal = await Promise.all(
        encryptedata?.legal?.map((e) => {
          return decryptLegal(e, private_key);
        })
      );
      new_data.assets = new_assets;
      new_data.beneficiaries = new_beneficiaries;
      new_data.trustees = new_trustees;
      new_data.issues = await decryptIssues(encryptedata.issues, private_key);
      new_data.legal = new_legal;
      /*
        Custom financial-exposure types. decryptExposureOptions never throws and
        returns [] when the field is absent, so users who predate the feature
        simply get an empty dropdown rather than a failed decrypt.
      */
      new_data.financial_exposure_custom_options = await decryptExposureOptions(
        private_key,
        encryptedata?.financial_exposure_custom_options
      );
      /*
        null (never chosen) is preserved as null so the table shows every
        column. Only an actual saved array narrows it.
      */
      new_data.asset_type_custom_options = await decryptExposureOptions(
        private_key,
        encryptedata?.asset_type_custom_options
      );
      new_data.asset_owner_custom_options = await decryptExposureOptions(
        private_key,
        encryptedata?.asset_owner_custom_options
      );
      const presented = await decScalar(
        private_key,
        encryptedata?.asset_management_presented_fields,
        null
      );
      new_data.asset_management_presented_fields = Array.isArray(presented)
        ? presented
        : null;
      new_data.partner_shared_files = await decryptPartnerFiles(
        private_key,
        encryptedata?.partner_shared_files
      );
      if (encryptedata?.profile_avatar) {
        const avatar_extension = encryptedata?.profile_avatar[0];
        if (avatar_extension == "/" || avatar_extension == "i") {
          //do nothing
        } else {
          new_data.profile_avatar = await decryptBase64file(
            private_key,
            encryptedata?.profile_avatar
          );
        }
      }
      console.log(new_data);
      resolve(new_data);
    } catch (e) {
      sendUIErrorToBackend(e)
      console.log(e);
      reject(e);
    }
  });
};
